import type {
  Database,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  MainContractorContact,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters } from '../../../types/claims';
import type { Document } from '../../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../../types/activity';
import { ProjectFoldersService } from '../../ProjectFoldersService';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import { geocodeAddress } from '../../../utils/geocoding';
import type { Constructor } from './mixins';
import { isAdministratorRole } from '../../../utils/permissions';

export const withCrm = <TBase extends Constructor<any>>(Base: TBase) => class Crm extends Base {
  // EMPLOYERS
  // ============================================================================

  public getEmployers(): Employer[] {
    return Object.values(this.db.employers || {});
  }

  public getEmployer(id: string): Employer | undefined {
    return this.db.employers?.[id];
  }

  public createEmployer(employer: Omit<Employer, 'id' | 'createdAt' | 'updatedAt'>): Employer {
    const id = this.generateId();
    const now = new Date().toISOString();
    const newEmployer: Employer = {
      ...employer,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.db.employers = this.db.employers || {};
    this.db.employers[id] = newEmployer;
    this.saveToStorage();
    this.notifyListeners();
    return newEmployer;
  }

  public updateEmployer(id: string, updates: Partial<Employer>): Employer | null {
    const employer = this.db.employers?.[id];
    if (!employer) return null;

    const updated: Employer = {
      ...employer,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.employers[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteEmployer(id: string): boolean {
    if (!this.db.employers?.[id]) return false;
    delete this.db.employers[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // CLIENT EMPLOYER RELATIONSHIPS
  // ============================================================================

  public getClientEmployerRelationships(): ClientEmployerRelationship[] {
    return Object.values(this.db.clientEmployerRelationships || {});
  }

  public getClientEmployerRelationship(id: string): ClientEmployerRelationship | undefined {
    return this.db.clientEmployerRelationships?.[id];
  }

  public getClientEmployerRelationshipsByClient(clientId: string): ClientEmployerRelationship[] {
    return this.getClientEmployerRelationships().filter(rel => rel.clientId === clientId);
  }

  public getClientEmployerRelationshipsByProject(projectId: string): ClientEmployerRelationship | undefined {
    return this.getClientEmployerRelationships().find(rel => rel.projectId === projectId);
  }

  public createClientEmployerRelationship(relationship: Omit<ClientEmployerRelationship, 'id' | 'createdAt' | 'updatedAt'>): ClientEmployerRelationship {
    const id = this.generateId();
    const now = new Date().toISOString();
    const newRelationship: ClientEmployerRelationship = {
      ...relationship,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.db.clientEmployerRelationships = this.db.clientEmployerRelationships || {};
    this.db.clientEmployerRelationships[id] = newRelationship;
    this.saveToStorage();
    this.notifyListeners();
    return newRelationship;
  }

  public updateClientEmployerRelationship(id: string, updates: Partial<ClientEmployerRelationship>): ClientEmployerRelationship | null {
    const relationship = this.db.clientEmployerRelationships?.[id];
    if (!relationship) return null;

    const updated: ClientEmployerRelationship = {
      ...relationship,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.clientEmployerRelationships[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteClientEmployerRelationship(id: string): boolean {
    if (!this.db.clientEmployerRelationships?.[id]) return false;
    delete this.db.clientEmployerRelationships[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // ENTITLEMENT ALERTS
  // ============================================================================

  public getEntitlementAlerts(): EntitlementAlert[] {
    return Object.values(this.db.entitlementAlerts || {});
  }

  public getEntitlementAlert(id: string): EntitlementAlert | undefined {
    return this.db.entitlementAlerts?.[id];
  }

  public getEntitlementAlertsByClient(clientId: string): EntitlementAlert[] {
    return this.getEntitlementAlerts().filter(alert => alert.clientId === clientId);
  }

  public getEntitlementAlertsByProject(projectId: string): EntitlementAlert[] {
    return this.getEntitlementAlerts().filter(alert => alert.projectId === projectId);
  }

  public getEntitlementAlertsBySeverity(severity: 'urgent' | 'high' | 'medium'): EntitlementAlert[] {
    return this.getEntitlementAlerts().filter(alert => alert.severity === severity);
  }

  public createEntitlementAlert(alert: Omit<EntitlementAlert, 'id' | 'createdAt' | 'updatedAt'>): EntitlementAlert {
    const id = this.generateId();
    const now = new Date().toISOString();
    const newAlert: EntitlementAlert = {
      ...alert,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.db.entitlementAlerts = this.db.entitlementAlerts || {};
    this.db.entitlementAlerts[id] = newAlert;
    this.saveToStorage();
    this.notifyListeners();
    return newAlert;
  }

  public updateEntitlementAlert(id: string, updates: Partial<EntitlementAlert>): EntitlementAlert | null {
    const alert = this.db.entitlementAlerts?.[id];
    if (!alert) return null;

    const updated: EntitlementAlert = {
      ...alert,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.entitlementAlerts[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteEntitlementAlert(id: string): boolean {
    if (!this.db.entitlementAlerts?.[id]) return false;
    delete this.db.entitlementAlerts[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // ACTIVE DISPUTES
  // ============================================================================

  public getActiveDisputes(): ActiveDispute[] {
    return Object.values(this.db.activeDisputes || {});
  }

  public getActiveDispute(id: string): ActiveDispute | undefined {
    return this.db.activeDisputes?.[id];
  }

  public getActiveDisputesByClient(clientId: string): ActiveDispute[] {
    return this.getActiveDisputes().filter(dispute => dispute.clientId === clientId);
  }

  public getActiveDisputesByProject(projectId: string): ActiveDispute[] {
    return this.getActiveDisputes().filter(dispute => dispute.projectId === projectId);
  }

  public getActiveDisputesByStatus(status: 'pre_adjudication' | 'adjudication' | 'arbitration' | 'litigation'): ActiveDispute[] {
    return this.getActiveDisputes().filter(dispute => dispute.status === status);
  }

  public createActiveDispute(dispute: Omit<ActiveDispute, 'id' | 'createdAt' | 'updatedAt'>): ActiveDispute {
    const id = this.generateId();
    const now = new Date().toISOString();
    const newDispute: ActiveDispute = {
      ...dispute,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.db.activeDisputes = this.db.activeDisputes || {};
    this.db.activeDisputes[id] = newDispute;
    this.saveToStorage();
    this.notifyListeners();
    return newDispute;
  }

  public updateActiveDispute(id: string, updates: Partial<ActiveDispute>): ActiveDispute | null {
    const dispute = this.db.activeDisputes?.[id];
    if (!dispute) return null;

    const updated: ActiveDispute = {
      ...dispute,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.activeDisputes[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteActiveDispute(id: string): boolean {
    if (!this.db.activeDisputes?.[id]) return false;
    delete this.db.activeDisputes[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // CLIENT REQUESTS
  // ============================================================================

  public getClientRequests(): ClientRequest[] {
    return Object.values(this.db.clientRequests || {});
  }

  public getClientRequest(id: string): ClientRequest | undefined {
    return this.db.clientRequests?.[id];
  }

  public getClientRequestsByStatus(status: 'pending' | 'approved' | 'rejected'): ClientRequest[] {
    return this.getClientRequests().filter(req => req.status === status);
  }

  public getClientRequestsByStaff(staffUserId: string): ClientRequest[] {
    return this.getClientRequests().filter(req => req.requestedBy === staffUserId);
  }

  /**
   * Creates a new client request and saves to Supabase backend
   * The request needs admin approval before the client is created
   * Workflow: Staff submits → Saved to backend → Admin reviews → Approved → Client created via createCompany()
   */
  public createClientRequest(request: Omit<ClientRequest, 'id' | 'status' | 'createdAt' | 'updatedAt'>): ClientRequest {
    const id = this.generateId();
    const now = new Date().toISOString();
    const newRequest: ClientRequest = {
      ...request,
      id,
      status: 'pending',
      createdAt: now,
      updatedAt: now,
    };
    this.db.clientRequests = this.db.clientRequests || {};
    this.db.clientRequests[id] = newRequest;
    this.saveToLocalStorage();
    
    // Save to Supabase backend
    this.saveEntityToBackend('client_requests', id, newRequest, true);
    
    // Create notifications for all admin users
    const adminUsers = this.getUsers().filter((u: any) => isAdministratorRole(u.role));

    adminUsers.forEach((admin: any) => {
      this.createNotification({
        userId: admin.id,
        type: 'approval_required',
        priority: 'high',
        title: 'New Client Request',
        message: `${request.requestedByName} has submitted a new client request for ${request.company_name}`,
        read: false,
        actionUrl: `/businesses?tab=requests&request=${id}`,
      });
    });
    
    this.notifyListeners();
    return newRequest;
  }

  public updateClientRequest(id: string, updates: Partial<ClientRequest>): ClientRequest | null {
    const request = this.db.clientRequests?.[id];
    if (!request) return null;

    const updated: ClientRequest = {
      ...request,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.clientRequests[id] = updated;
    this.saveToLocalStorage();
    
    // Save to backend
    this.saveEntityToBackend('client_requests', id, updated, false);
    
    this.notifyListeners();
    return updated;
  }

  public approveClientRequest(id: string, adminUserId: string, adminUserName: string, reviewNotes?: string): ClientRequest | null {
    const request = this.db.clientRequests?.[id];
    if (!request) return null;

    const updated: ClientRequest = {
      ...request,
      status: 'approved',
      reviewedBy: adminUserId,
      reviewedByName: adminUserName,
      reviewedAt: new Date().toISOString(),
      reviewNotes,
      updatedAt: new Date().toISOString(),
    };
    this.db.clientRequests[id] = updated;
    this.saveToLocalStorage();
    
    // Save to backend
    this.saveEntityToBackend('client_requests', id, updated, false);
    
    // Notify the staff member who submitted the request
    this.createNotification({
      userId: request.requestedBy,
      type: 'approval_approved',
      priority: 'medium',
      title: 'Client Request Approved',
      message: `${adminUserName} approved your client request for ${request.company_name}`,
      read: false,
      actionUrl: `/businesses?tab=clients`,
    });
    
    this.notifyListeners();
    return updated;
  }

  public rejectClientRequest(id: string, adminUserId: string, adminUserName: string, reviewNotes: string): ClientRequest | null {
    const request = this.db.clientRequests?.[id];
    if (!request) return null;

    console.log(`❌ Rejecting client request ${id} by ${adminUserName}`);

    const updated: ClientRequest = {
      ...request,
      status: 'rejected',
      reviewedBy: adminUserId,
      reviewedByName: adminUserName,
      reviewedAt: new Date().toISOString(),
      reviewNotes,
      updatedAt: new Date().toISOString(),
    };
    this.db.clientRequests[id] = updated;
    this.saveToLocalStorage();
    
    // Save to backend
    this.saveEntityToBackend('client_requests', id, updated, false);
    
    // Notify the staff member who submitted the request
    const notification = this.createNotification({
      userId: request.requestedBy,
      type: 'approval_rejected',
      priority: 'high',
      title: 'Client Request Rejected',
      message: `${adminUserName} rejected your client request for ${request.company_name}${reviewNotes ? `: ${reviewNotes}` : ''}`,
      read: false,
      actionUrl: `/businesses?tab=requests&request=${id}`,
    });
    
    console.log(`   📬 Sent rejection notification ${notification.id} to staff member ${request.requestedByName}`);
    
    this.notifyListeners();
    return updated;
  }

  public deleteClientRequest(id: string): boolean {
    if (!this.db.clientRequests?.[id]) return false;
    delete this.db.clientRequests[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // CONTACTS (CRM)
  // ============================================================================

  public getContacts(): Contact[] {
    if (!this.db.contacts) {
      this.db.contacts = {};
    }
    return Object.values(this.db.contacts);
  }

  public getContact(id: string): Contact | undefined {
    if (!this.db.contacts) {
      this.db.contacts = {};
    }
    return this.db.contacts[id];
  }

  public getContactsByCompany(companyId: string): Contact[] {
    return this.getContacts().filter(c => c.companyId === companyId);
  }

  public getContactsByMainContractor(mainContractorId: string): Contact[] {
    return this.getContacts().filter(c => c.mainContractorId === mainContractorId);
  }

  public getContactsByType(contactType: string): Contact[] {
    return this.getContacts().filter(c => c.contactType === contactType);
  }

  public createContact(contact: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>): Contact {
    if (!this.db.contacts) {
      this.db.contacts = {};
    }
    
    const newContact: Contact = {
      ...contact,
      id: this.generateId(),
      fullName: `${contact.firstName} ${contact.lastName}`.trim(),
      totalInteractions: 0,
      activeProjectIds: contact.activeProjectIds || [],
      pastProjectIds: contact.pastProjectIds || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    this.db.contacts[newContact.id] = newContact;
    this.saveToStorage();
    this.notifyListeners();
    return newContact;
  }

  public updateContact(id: string, updates: Partial<Contact>): Contact | undefined {
    if (!this.db.contacts) {
      this.db.contacts = {};
    }
    
    const contact = this.db.contacts[id];
    if (!contact) return undefined;
    
    // Update fullName if first/last name changed
    const firstName = updates.firstName || contact.firstName;
    const lastName = updates.lastName || contact.lastName;
    const fullName = `${firstName} ${lastName}`.trim();
    
    const updated = {
      ...contact,
      ...updates,
      id, // Prevent ID change
      fullName,
      updatedAt: new Date().toISOString(),
    };
    
    this.db.contacts[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteContact(id: string): boolean {
    if (!this.db.contacts) {
      this.db.contacts = {};
    }
    
    if (!this.db.contacts[id]) return false;
    delete this.db.contacts[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // MAIN CONTRACTORS
  // ============================================================================

  public getMainContractors(): MainContractor[] {
    if (!this.db.mainContractors) {
      this.db.mainContractors = {};
    }
    return Object.values(this.db.mainContractors);
  }

  public getMainContractor(id: string): MainContractor | undefined {
    if (!this.db.mainContractors) {
      this.db.mainContractors = {};
    }
    return this.db.mainContractors[id];
  }

  public getMainContractorByName(name: string): MainContractor | undefined {
    const normalized = name.trim().toLowerCase();
    return this.getMainContractors().find(mc => mc.name.toLowerCase() === normalized);
  }

  public getActiveMainContractors(): MainContractor[] {
    return this.getMainContractors().filter(mc => mc.status === 'active');
  }

  public getMainContractorsByStatus(status: 'active' | 'inactive' | 'blacklisted'): MainContractor[] {
    return this.getMainContractors().filter(mc => mc.status === status);
  }

  public searchMainContractors(query: string): MainContractor[] {
    const lowerQuery = query.toLowerCase();
    return this.getMainContractors().filter(mc =>
      mc.name.toLowerCase().includes(lowerQuery) ||
      mc.postcode?.toLowerCase().includes(lowerQuery) ||
      mc.email?.toLowerCase().includes(lowerQuery) ||
      mc.companyNumber?.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Check if a company number is already registered to a different main contractor
   * @param companyNumber The company number to check
   * @param excludeId Optional ID to exclude from the check (for updates)
   * @returns The existing contractor if found, undefined otherwise
   */
  public findMainContractorByCompanyNumber(companyNumber: string, excludeId?: string): MainContractor | undefined {
    if (!companyNumber || !companyNumber.trim()) {
      return undefined;
    }

    const normalizedNumber = companyNumber.trim().toUpperCase();
    const contractors = this.getMainContractors();
    
    return contractors.find(mc => {
      // Skip if this is the contractor being edited
      if (excludeId && mc.id === excludeId) {
        return false;
      }
      
      // Check if company numbers match (case-insensitive)
      return mc.companyNumber && mc.companyNumber.trim().toUpperCase() === normalizedNumber;
    });
  }

  public createMainContractor(contractor: Omit<MainContractor, 'id' | 'createdAt' | 'updatedAt'>): MainContractor {
    if (!this.db.mainContractors) {
      this.db.mainContractors = {};
    }
    
    // Check for duplicate company number
    if (contractor.companyNumber) {
      const existing = this.findMainContractorByCompanyNumber(contractor.companyNumber);
      if (existing) {
        console.error('❌ Duplicate company number found!');
        throw new Error(`A main contractor with company number ${contractor.companyNumber} already exists: ${existing.name}`);
      }
    }
    
    const newContractor: MainContractor = {
      ...contractor,
      id: this.generateId(),
      activeProjects: contractor.activeProjects || 0,
      completedProjects: contractor.completedProjects || 0,
      totalValueWorked: contractor.totalValueWorked || 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    this.db.mainContractors[newContractor.id] = newContractor;
    this.saveToStorage();
    this.notifyListeners();
    
    // Save to backend - extract contacts for separate table
    const { contacts, ...contractorWithoutContacts } = newContractor;

    // IMPORTANT: Save main contractor first, then contacts (to avoid FK constraint violation)
    this.trackLocalWrite('mainContractors', newContractor.id, (async () => {
      try {
        // Save main contractor and wait for it to complete
        await this.saveEntityToBackend('mainContractors', newContractor.id, contractorWithoutContacts, true);
        
        // Now save contacts after main contractor is created
        if (contacts && contacts.length > 0) {
          for (const contact of contacts) {
            const contactData = {
              ...contact,
              mainContractorId: newContractor.id,
            };
            await this.saveEntityToBackend('mainContractorContacts', contact.id, contactData, true);
          }
        }
      } catch (error) {
        console.error(`❌ [BACKEND] Error saving main contractor ${newContractor.id} to backend:`, error);
      }
    })());

    return newContractor;
  }

  public updateMainContractor(id: string, updates: Partial<MainContractor>): MainContractor | undefined {
    if (!this.db.mainContractors) {
      this.db.mainContractors = {};
    }
    
    const contractor = this.db.mainContractors[id];
    if (!contractor) {
      console.error('❌ Contractor not found with ID:', id);
      return undefined;
    }
    
    // Check for duplicate company number if it's being updated
    if (updates.companyNumber) {
      const existing = this.findMainContractorByCompanyNumber(updates.companyNumber, id);
      if (existing) {
        console.error('❌ Duplicate company number found!');
        throw new Error(`A main contractor with company number ${updates.companyNumber} already exists: ${existing.name}`);
      }
    }
    
    const updated = {
      ...contractor,
      ...updates,
      id, // Prevent ID change
      updatedAt: new Date().toISOString(),
    };
    
    this.db.mainContractors[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    
    // Save to backend - extract contacts for separate table
    const { contacts, ...contractorWithoutContacts } = updated;
    
    // IMPORTANT: Save main contractor first, then contacts (to avoid FK constraint violation)
    this.trackLocalWrite('mainContractors', id, (async () => {
      try {
        // Save main contractor and wait for it to complete
        await this.saveEntityToBackend('mainContractors', id, contractorWithoutContacts, false);
        
        // If contacts were updated, handle them separately
        if (updates.contacts !== undefined && contacts) {
          // Upsert all contacts (backend will update existing or create new)
          for (const contact of contacts) {
            const contactData = {
              ...contact,
              mainContractorId: id,
            };
            // Use POST with upsert - backend will handle update vs create
            await this.saveEntityToBackend('mainContractorContacts', contact.id, contactData, true);
          }
        }
      } catch (error) {
        console.error(`❌ Error saving main contractor update to backend:`, error);
      }
    })());
    
    return updated;
  }

  /**
   * Remove a single contact from a main contractor. updateMainContractor with a
   * filtered contacts array only upserts the survivors, so the removed row would
   * linger orphaned in main_contractor_contacts. This issues a real backend
   * DELETE so local state and the DB stay in sync.
   */
  public removeMainContractorContact(mainContractorId: string, contactId: string): MainContractor | undefined {
    const contractor = this.db.mainContractors?.[mainContractorId];
    if (!contractor) {
      console.error('❌ Contractor not found with ID:', mainContractorId);
      return undefined;
    }

    const updated = {
      ...contractor,
      contacts: (contractor.contacts ?? []).filter((c: MainContractorContact) => c.id !== contactId),
      updatedAt: new Date().toISOString(),
    };

    this.db.mainContractors[mainContractorId] = updated;
    this.saveToStorage();
    this.notifyListeners();

    this.trackLocalWrite('mainContractors', mainContractorId, this.deleteEntityFromBackend('mainContractorContacts', contactId));

    return updated;
  }

  public deleteMainContractor(id: string): boolean {
    if (!this.db.mainContractors) {
      this.db.mainContractors = {};
    }
    
    if (!this.db.mainContractors[id]) return false;
    delete this.db.mainContractors[id];
    this.saveToStorage();
    this.notifyListeners();
    
    // Delete from backend
    this.deleteEntityFromBackend('mainContractors', id);
    
    return true;
  }

  // ============================================================================
};
